import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { janelaAteHoje } from '@/lib/dia-da-operacao';
import { formatarAteDuasCasas, formatarDia, formatarInteiro } from '@/lib/formato';
import { retornoDe, type AlvoDoRetorno } from '@/lib/retorno';
import { ehFichaAlterada } from '@/lib/versao-da-ficha';

import {
  api,
  ApiIndisponivel,
  RecusaDaApi,
  SemSessao,
  type EnvioDeApontamento,
  type EnvioDeMotivo,
  type EnvioDeResolucao,
  type FiltroDaFila,
  type Filtro,
  type TurnoDaApi,
  type ApontamentoDaApi,
  type CadastrosDaApi,
  type DivergenciaDaApi,
  type LoteDaApi,
} from './api';
import { DIAS_DO_ULTIMO_TURNO, ultimoTurnoDaMaquina } from './apontamento-sugerido';

/**
 * O módulo 03 falando em domínio.
 *
 * A chave carrega o FILTRO, e não é uma só: as três listas da tela respondem a
 * períodos diferentes e a máquina escolhida, então uma chave única faria a
 * troca de filtro devolver o resultado do filtro anterior enquanto o novo
 * chega — o pior formato de dado errado, porque parece certo.
 *
 * Já a INVALIDAÇÃO é grossa: apontar um turno muda a lista, muda as horas do
 * período e pode mudar o que está sem conferir. Invalidar só a lista deixaria
 * os indicadores do topo com o número anterior, e ninguém entende por que o
 * total não bateu com as linhas embaixo dele.
 */
const CHAVE = ['operacao'] as const;

/* O "em quê" do retorno (R04, p.32), lido do que a tela já carregou: a
   resposta das escritas da operação traz só o id ou uma contagem. */
function maquinaDosCadastros(cliente: QueryClient, id: string): AlvoDoRetorno | undefined {
  const e = cliente.getQueryData<CadastrosDaApi>([...CHAVE, 'cadastros'])?.equipamentos.find((x) => x.id === id);
  return e ? { codigo: e.codigo, nome: e.descricao } : undefined;
}

function achadoNasListas<T extends { readonly id: string }>(cliente: QueryClient, prefixo: string, campo: string, id: string): T | undefined {
  for (const [, dado] of cliente.getQueriesData<Record<string, unknown> | readonly T[]>({ queryKey: [...CHAVE, prefixo] })) {
    const lista = Array.isArray(dado) ? (dado as readonly T[]) : ((dado as Record<string, unknown> | undefined)?.[campo] as readonly T[] | undefined);
    const achado = lista?.find((x) => x.id === id);
    if (achado) return achado;
  }
  return undefined;
}

const horas = (n: number) => `${formatarAteDuasCasas(n)} h`;

/** `ligada: false` para quem não lê a operação (o formulário de situação da mesa: `equipamentos/leitura-do-detalhe.ts`). */
export function useCadastrosDaOperacao(ligada = true) {
  return useQuery({
    queryKey: [...CHAVE, 'cadastros'],
    queryFn: () => api.cadastros(),
    enabled: ligada,
  });
}

export function useApontamentos(filtro: Filtro) {
  return useQuery({
    queryKey: [...CHAVE, 'apontamentos', filtro],
    queryFn: async () => (await api.apontamentos(filtro)).apontamentos,
  });
}

/**
 * O ÚLTIMO TURNO DA MÁQUINA, para o apontamento propor o operador habitual, o
 * nome do turno e o horímetro do começo (pedido do dono, 31/10/2026). Sai só
 * com a máquina escolhida, e olha os últimos `DIAS_DO_ULTIMO_TURNO` dias. Sem
 * `retry`: sem rede, o formulário segue em branco, como era.
 */
export function useUltimoTurnoDaMaquina(equipamentoId: string) {
  return useQuery({
    queryKey: [...CHAVE, 'ultimo-turno', equipamentoId],
    enabled: equipamentoId !== '',
    retry: false,
    queryFn: async () => {
      const { de, ate } = janelaAteHoje(DIAS_DO_ULTIMO_TURNO);
      const r = await api.apontamentos({ de, ate, equipamentoId });
      return ultimoTurnoDaMaquina(r.apontamentos, equipamentoId);
    },
  });
}

/**
 * Os turnos estornados do recorte (R06, p.69). A chave mora sob `CHAVE`: o
 * estorno e a correção, que invalidam a operação inteira, a refazem também.
 */
export function useTurnosEstornados(filtro: Filtro) {
  return useQuery({
    queryKey: [...CHAVE, 'estornados', filtro],
    queryFn: () => api.turnosEstornados(filtro),
  });
}

export function useHoras(filtro: Filtro) {
  return useQuery({
    queryKey: [...CHAVE, 'horas', filtro],
    queryFn: () => api.horas(filtro),
    select: (d) => d.horas,
  });
}

/** A janela programada do mesmo pedido de horas: mesma chave, uma ida só ao servidor. */
export function useJanela(filtro: Filtro) {
  return useQuery({
    queryKey: [...CHAVE, 'horas', filtro],
    queryFn: () => api.horas(filtro),
    select: (d) => d.janela ?? null,
  });
}

/**
 * O QUE SAIU PARA A OBRA E NAO REPORTOU (0112).
 *
 * `enabled` pela permissao: sem `operacao.ler` a consulta nao sai, e a tela
 * mostra o estado sem permissao em vez de uma lista vazia — que afirmaria que
 * nada saiu, o que ninguem sabe.
 *
 * Recarrega a cada 5 minutos enquanto a tela esta aberta: a hora sem reportar
 * cresce sozinha, e um numero congelado num painel de cobranca e pior que
 * nenhum. Fora da tela, nao recarrega.
 */
export function useSaidasParaAObra(permitido: boolean) {
  return useQuery({
    queryKey: [...CHAVE, 'saidas'],
    queryFn: async () => (await api.saidas()).saidas,
    enabled: permitido,
    refetchInterval: 5 * 60_000,
    refetchIntervalInBackground: false,
  });
}

/**
 * As leituras em volta do turno e se quem lança é apto (achado dos robôs,
 * 20/10/2026). Sem `retry`: sem rede o formulário segue com a última leitura
 * conhecida, e não espera.
 */
export function useLeiturasDoTurno(equipamentoId: string, dia: string, turno: string, intervalos = '') {
  return useQuery({
    /* Os INTERVALOS na chave (rodada 3): o fim do turno e o fim do ultimo
       intervalo, e mudar o horario muda a leitura de antes e a de depois. */
    queryKey: [...CHAVE, 'leituras-do-turno', equipamentoId, dia, turno, intervalos],
    enabled: equipamentoId !== '' && turno !== '',
    retry: false,
    /* Enquanto a nova resposta vem, a da mesma maquina segue (o operador
       padrao nao pisca); o numero sugerido espera — ver `medidorInicialSugerido`. */
    placeholderData: (anterior, consulta) => (consulta?.queryKey[CHAVE.length + 1] === equipamentoId ? anterior : undefined),
    queryFn: () => api.leiturasDoTurno(equipamentoId, dia, turno, intervalos),
  });
}

export function useTurnos() {
  return useQuery({ queryKey: [...CHAVE, 'turnos'], queryFn: async () => (await api.turnos()).turnos });
}

export function useGravarTurnos() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<{ turnos: readonly TurnoDaApi[] }, { motivo: string }>((r, v) => ({
        titulo: 'Turnos da empresa gravados',
        alvo: { nome: 'Calendário de turnos' },
        fatos: [`${formatarInteiro(r.turnos.length)} ${r.turnos.length === 1 ? 'turno' : 'turnos'} na semana`, `motivo: ${v.motivo}`],
        efeito: 'As horas programadas passam a contar por este calendário',
      })),
    },
    mutationFn: ({ turnos, motivo }: { turnos: readonly TurnoDaApi[]; motivo: string }) => api.gravarTurnos(turnos, motivo),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE });
    },
  });
}

export function useParadas(filtro: Filtro) {
  return useQuery({
    queryKey: [...CHAVE, 'paradas', filtro],
    queryFn: async () => (await api.paradas(filtro)).paradas,
  });
}

export function useMotivos() {
  return useQuery({
    queryKey: [...CHAVE, 'motivos'],
    queryFn: async () => (await api.motivos()).itens,
  });
}

export function useGravarMotivo() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<{ codigo: string; nome: string; evitavel: boolean; ativo: boolean }, { id: string | null }>((m, v) => ({
        titulo: v.id === null ? 'Causa de parada cadastrada' : m.ativo ? 'Causa de parada alterada' : 'Causa de parada arquivada',
        alvo: { codigo: m.codigo, nome: m.nome },
        fatos: [m.evitavel ? 'evitável' : 'não evitável'],
        ...(v.id === null ? { efeito: 'Já aparece na escolha da causa, na situação da máquina' } : {}),
      })),
    },
    mutationFn: ({ id, envio }: { id: string | null; envio: EnvioDeMotivo }) =>
      api.gravarMotivo(id, envio),
    /* Invalida a CHAVE inteira: a causa nova precisa aparecer no seletor do
       formulário de situação, que lê `/operacao/cadastros` — outra consulta.
       Invalidar só a lista deixaria o seletor sem a causa recém-criada, e a
       pessoa cadastraria a mesma duas vezes. */
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
    /* Recusada por versao velha (robos de 20/10/2026): a lista rele, e reabrir mostra a de agora. */
    onError: (erro) => {
      if (ehFichaAlterada(erro)) void cliente.invalidateQueries({ queryKey: CHAVE });
    },
  });
}

export function useApontar() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, EnvioDeApontamento>((_r, v) => {
        const alvo = maquinaDosCadastros(cliente, v.equipamentoId);
        return {
          titulo: 'Turno apontado',
          ...(alvo ? { alvo } : {}),
          fatos: [
            `${formatarDia(v.dia)} · ${v.turno}`,
            `horímetro ${formatarAteDuasCasas(v.horimetroInicial)} a ${formatarAteDuasCasas(v.horimetroFinal)}`,
            `${horas(v.horasProducao)} de produção`,
          ],
          efeito: 'Fica sem conferir até alguém conferir o turno',
        };
      }),
    },
    mutationFn: (envio: EnvioDeApontamento) => api.apontar(envio),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useConferir() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { id: string; motivo?: string }>((_r, v) => {
        const a = achadoNasListas<ApontamentoDaApi>(cliente, 'apontamentos', 'apontamentos', v.id);
        return {
          titulo: 'Turno conferido',
          ...(a ? { alvo: { codigo: a.equipamento, nome: a.equipamentoDescricao } } : {}),
          fatos: [a ? `${formatarDia(a.dia)} · ${a.turno}` : null, v.motivo ? `motivo: ${v.motivo}` : null],
        };
      }),
    },
    mutationFn: ({ id, motivo }: { id: string; motivo?: string }) => api.conferir(id, motivo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

/* ------------------------------------------- estornar e corrigir (ADR-008, 0086) */

/** O "em quê" de um turno já carregado: a máquina e o dia · turno. */
function turnoDasListas(cliente: QueryClient, id: string) {
  const a = achadoNasListas<ApontamentoDaApi>(cliente, 'apontamentos', 'apontamentos', id);
  return a
    ? { alvo: { codigo: a.equipamento, nome: a.equipamentoDescricao } as AlvoDoRetorno, quando: `${formatarDia(a.dia)} · ${a.turno}` }
    : null;
}

export function useEstornarApontamento() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { id: string; motivo: string }>((_r, v) => {
        const t = turnoDasListas(cliente, v.id);
        return {
          titulo: 'Turno estornado',
          ...(t ? { alvo: t.alvo } : {}),
          fatos: [t?.quando ?? null, `motivo: ${v.motivo}`],
          efeito: 'Saiu das horas do período, com a leitura de horímetro que ele gravou',
        };
      }),
    },
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) => api.estornar(id, motivo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useCorrigirApontamento() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { id: string; motivo: string; apontamento: EnvioDeApontamento }>((_r, v) => {
        const t = turnoDasListas(cliente, v.id);
        const alvo = t?.alvo ?? maquinaDosCadastros(cliente, v.apontamento.equipamentoId);
        return {
          titulo: 'Turno corrigido',
          ...(alvo ? { alvo } : {}),
          fatos: [
            `${formatarDia(v.apontamento.dia)} · ${v.apontamento.turno}`,
            `horímetro ${formatarAteDuasCasas(v.apontamento.horimetroInicial)} a ${formatarAteDuasCasas(v.apontamento.horimetroFinal)}`,
            `motivo: ${v.motivo}`,
          ],
          efeito: 'O turno errado saiu das somas; o certo fica sem conferir até alguém conferir',
        };
      }),
    },
    mutationFn: ({ id, motivo, apontamento }: { id: string; motivo: string; apontamento: EnvioDeApontamento }) =>
      api.corrigir(id, motivo, apontamento),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

/* ------------------------------------------------------------------ OPE 03 */
/* Toda escrita da conciliação invalida a CHAVE inteira: promover um lote muda a
   lista de lotes, a fila e os números do topo ao mesmo tempo. */

export function useImportacoes() {
  return useQuery({ queryKey: [...CHAVE, 'importacoes'], queryFn: () => api.importacoes() });
}

export function useImportacao(id: string | null) {
  return useQuery({
    queryKey: [...CHAVE, 'importacoes', id],
    queryFn: () => api.importacao(id ?? ''),
    enabled: id !== null,
  });
}

export function useImportar() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<Awaited<ReturnType<typeof api.importar>>, { nomeArquivo: string }>((r, v) => ({
        titulo: 'Planilha recebida para conferir',
        tom: r.linhasValidas < r.linhas ? 'ressalva' : 'sucesso',
        alvo: { nome: v.nomeArquivo },
        fatos: [
          `${formatarInteiro(r.linhas)} ${r.linhas === 1 ? 'linha' : 'linhas'}`,
          r.linhasValidas < r.linhas ? `${formatarInteiro(r.linhas - r.linhasValidas)} com problema` : null,
        ],
        efeito: 'Nada entra nas horas antes de você promover o lote',
      })),
    },
    mutationFn: ({ nomeArquivo, conteudo }: { nomeArquivo: string; conteudo: string }) => api.importar(nomeArquivo, conteudo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function usePromover() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<Awaited<ReturnType<typeof api.promover>>, string>((r, id) => {
        const lote = achadoNasListas<LoteDaApi>(cliente, 'importacoes', 'lotes', id);
        return {
          titulo: 'Lote promovido',
          tom: r.divergenciasAbertas > 0 ? 'ressalva' : 'sucesso',
          ...(lote ? { alvo: { nome: lote.nomeArquivo } } : {}),
          fatos: [
            `${formatarInteiro(r.promovidas)} ${r.promovidas === 1 ? 'linha entrou' : 'linhas entraram'}`,
            `${formatarDia(r.periodo.de)} a ${formatarDia(r.periodo.ate)}`,
          ],
          efeito:
            r.divergenciasAbertas > 0
              ? `${formatarInteiro(r.divergenciasAbertas)} ${r.divergenciasAbertas === 1 ? 'divergência aberta' : 'divergências abertas'} na fila`
              : 'Nenhuma divergência aberta no período',
        };
      }),
    },
    mutationFn: (id: string) => api.promover(id),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useDescartar() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { id: string; motivo: string }>((_r, v) => {
        const lote = achadoNasListas<LoteDaApi>(cliente, 'importacoes', 'lotes', v.id);
        return {
          titulo: 'Lote descartado',
          ...(lote ? { alvo: { nome: lote.nomeArquivo } } : {}),
          fatos: [lote ? `${formatarInteiro(lote.linhas)} linhas` : null, `motivo: ${v.motivo}`],
          efeito: 'Nenhuma linha dele entrou nas horas',
        };
      }),
    },
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) => api.descartar(id, motivo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useDivergencias(filtro: FiltroDaFila) {
  return useQuery({ queryKey: [...CHAVE, 'divergencias', filtro], queryFn: () => api.divergencias(filtro) });
}

export function useApurar() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<Awaited<ReturnType<typeof api.apurar>>, { de: string; ate: string }>((r, v) => ({
        titulo: 'Conciliação apurada',
        alvo: { nome: `${formatarDia(v.de)} a ${formatarDia(v.ate)}` },
        efeito:
          r.divergenciasAbertas > 0
            ? `${formatarInteiro(r.divergenciasAbertas)} ${r.divergenciasAbertas === 1 ? 'divergência aberta' : 'divergências abertas'} na fila`
            : 'Nenhuma divergência aberta no período',
      })),
    },
    mutationFn: ({ de, ate }: { de: string; ate: string }) => api.apurar(de, ate),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useResolver() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { id: string; envio: EnvioDeResolucao }>((_r, v) => {
        const d = achadoNasListas<DivergenciaDaApi>(cliente, 'divergencias', 'divergencias', v.id);
        return {
          titulo: 'Divergência resolvida',
          ...(d ? { alvo: { codigo: d.equipamento, nome: formatarDia(d.dia) } } : {}),
          fatos: [v.envio.resolucao, d ? `diferença de ${horas(d.diferencaH)}` : null],
          efeito: 'Saiu da fila de divergências abertas',
        };
      }),
    },
    mutationFn: ({ id, envio }: { id: string; envio: EnvioDeResolucao }) => api.resolver(id, envio),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

/**
 * Atribuir (ou tirar) o responsavel pela divergencia.
 *
 * Invalida a MESMA chave da fila: o nome do responsavel aparece na lista e no
 * painel, e deixar a fila com o valor velho faria a atribuicao parecer que nao
 * pegou — a pessoa atribuiria de novo.
 */
export function useAtribuirResponsavel() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, responsavelId }: { id: string; responsavelId: string | null }) =>
      api.atribuir(id, responsavelId),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
    meta: { retorno: retornoDaAtribuicao(cliente) },
  });
}

/* O retorno da atribuicao (R04, p.32): a divergencia e quem ficou com ela. */
function retornoDaAtribuicao(cliente: QueryClient) {
  return retornoDe<unknown, { id: string; responsavelId: string | null }>((_r, v) => {
    const d = achadoNasListas<DivergenciaDaApi>(cliente, 'divergencias', 'divergencias', v.id);
    const pessoa = cliente.getQueryData<CadastrosDaApi>([...CHAVE, 'cadastros'])?.operadores.find((o) => o.id === v.responsavelId);
    return {
      titulo: v.responsavelId === null ? 'Responsável retirado da divergência' : 'Responsável atribuído',
      ...(d ? { alvo: { codigo: d.equipamento, nome: formatarDia(d.dia) } } : {}),
      fatos: [pessoa ? pessoa.nome : null],
    };
  });
}

export function useTolerancia() {
  return useQuery({
    queryKey: [...CHAVE, 'tolerancia'],
    queryFn: async () => (await api.tolerancia()).toleranciaH,
  });
}

export function useGravarTolerancia() {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<{ toleranciaH: number }, { motivo: string }>((r, v) => ({
        titulo: 'Tolerância da conciliação gravada',
        alvo: { nome: 'Conciliação de horas' },
        fatos: [horas(r.toleranciaH), `motivo: ${v.motivo}`],
        efeito: 'Vale para a próxima apuração',
      })),
    },
    mutationFn: ({ toleranciaH, motivo }: { toleranciaH: number; motivo: string }) => api.gravarTolerancia(toleranciaH, motivo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

/**
 * A falha virada frase que diz o que fazer.
 *
 * `SemSessao` tem saída própria porque a genérica ("tente de novo") nunca
 * funcionaria: o que falta é entrar outra vez, e o formulário continua
 * preenchido enquanto isso.
 */
export function explicarFalha(erro: unknown): { readonly erro: string; readonly comoResolver: string } {
  if (erro instanceof SemSessao) {
    return {
      erro: 'sua sessão expirou',
      comoResolver: 'Entre outra vez. O que você digitou continua na tela.',
    };
  }
  if (erro instanceof ApiIndisponivel) {
    return {
      erro: 'o sistema não respondeu',
      comoResolver: 'Confira a conexão e tente de novo; se continuar, avise o suporte com o horário.',
    };
  }
  if (erro instanceof RecusaDaApi) {
    return { erro: erro.message, comoResolver: erro.comoResolver };
  }
  return {
    erro: 'não foi possível falar com o servidor',
    comoResolver: 'Tente de novo; se continuar, avise o suporte com o horário em que aconteceu.',
  };
}
